import { defineStore } from 'pinia';
import { ProductService } from '@/services/product';
import { ContentService } from '@/services/content';
import moment from 'moment';
import { useGlimpseContentStore } from '@/stores/glimpseContent';
import { resolveLegacyNamespace } from '@/stores/legacyBridge';
/*
    Hält Drafts der Inhalte einzelner Bot Antworten im State.

    updated ist notwendig zum Synchronisieren des States,
    da direkt an den State Objekten die Bindings gemacht werden.
*/

/** Per-bot deployment info */
export interface DeploymentInfo {
  /** Whether there are unpublished changes that can be deployed */
  isDeployable: boolean;
  /** When the bot was last published; `false` when unknown */
  lastPublishDate: Date | string | false;
  /** When content was last changed; `false` when unknown */
  lastChangeDate: Date | string | false;
}

/** State of the contentStore module (registered as `content`) */
export interface ContentStoreState {
  /** Draft content boxes: {[botId]: {[intent]: {[boxId]: box}}} */
  contentDrafts: Record<string, Record<string, Record<string, any>>>;
  /** Timestamp (ms) of the last update, bumped to trigger re-render of direct state bindings */
  updated: number;
  /** Change flags: {[botId]: {[intent]: {[String(channel)]: {changed}}}} */
  updates: Record<string, Record<string, Record<string, { changed: boolean | null }>>>;
  /** Deployment info per bot ID */
  deployment: Record<string, DeploymentInfo>;
  /** Whether the AI assistant flow is active for the content editor */
  isAssistant: boolean;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/contentStore.ts` (namespace
 * `content` — NOT to be confused with the sibling `glimpseContent` module, which lives at
 * `src/store/modules/content.ts` / `src/stores/glimpseContent.ts`). Persisted to `localStorage`
 * (full state) — matches the legacy `vuexLocal` reducer's `content: state.content` entry in
 * `src/store/index.ts`.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - State prop `isAssistant` and getter `isAssistant` (an identity passthrough) collide — the
 *   getter is renamed `isAssistantGetter`; consumers wanting the raw flag should just read
 *   `store.isAssistant` directly (the state prop keeps its name).
 * - Action `update` and mutation `update` share a name. Unlike the "action merely commits the
 *   mutation" case, legacy `update` (the *action*) computed `changed` itself via
 *   `glimpseContent/isContentChanged` and then committed the mutation with that computed value —
 *   while sibling actions in this very module (`save`, `resetDraft`, `setContentDraftIntent`)
 *   called `commit('update', {..., changed: <explicit value>})` DIRECTLY, bypassing the action's
 *   computation. Merged into one action: if `changed` is omitted from the payload it is computed
 *   exactly like the legacy action did; if it is supplied, it is used as-is exactly like a direct
 *   mutation commit did. Behavior is identical for both call sites.
 * - All other mutations (`setValue`, `resetUpdate`, `resetDrafts`, `setContentDraft`,
 *   `setContentDrafts`, `setDeployment`) had no same-named action, so they become same-named
 *   actions directly; other actions that used to `commit(...)` them now call `this.<name>(...)`.
 *
 * Cross-store references (see STORES.md):
 * - `rootGetter['glimpseContent/isContentChanged']` / `rootGetters['glimpseContent/getContentBoxes']`
 *   → `useGlimpseContentStore().isContentChanged(...)` / `useGlimpseContentStore().getContentBoxes(...)`.
 * - `commit('glimpseContent/resetOrigins', ..., { root: true })` →
 *   `useGlimpseContentStore().resetOrigins(...)`.
 * - `rootGetters['bots/currentBotId']` — the `bots` module has not been converted to Pinia yet, so
 *   this is resolved through the legacy bridge registry (`resolveLegacyNamespace('bots')`) instead
 *   of a direct `useBotsStore()` import, exactly like `preferences.ts`'s `suggestedChannelId`
 *   getter does today. TODO: swap to `useBotsStore()` once `src/stores/bots.ts` exists.
 */
export const useContentStore = defineStore('content', {
  state: (): ContentStoreState => ({
    contentDrafts: {}, // cache for drafts
    updated: 0,
    updates: {},
    deployment: {},
    isAssistant: false,
  }),
  getters: {
    isDeployable(): (botId: string) => boolean {
      return (botId: string) => {
        if (!this.deployment[botId]) {
          return false;
        }

        return this.deployment[botId].isDeployable;
      };
    },
    lastPublishDate(): (botId: string) => Date | string | false {
      return (botId: string) => {
        if (!this.deployment[botId]) {
          return false;
        }

        return this.deployment[botId].lastPublishDate;
      };
    },
    lastChangeDate(): (botId: string) => Date | string | false {
      return (botId: string) => {
        if (!this.deployment[botId]) {
          return false;
        }

        return this.deployment[botId].lastChangeDate;
      };
    },
    getContentBoxes(): (botId: string, intent: string, channel?: string | false) => any[] {
      return (botId: string, intent: string, channel?: string | false) => {
        if (!this.contentDrafts?.[botId]?.[intent]) {
          return [];
        }

        const allBoxes = Object.values(this.contentDrafts[botId][intent]).filter((box: any) => {
          if (!channel && box.channel) return false;
          if (!channel && !box.channel) return true;
          if (channel && box.channel && channel === box.channel) return true;
          return false;
        });

        return allBoxes;
      };
    },
    isContentChanged(): (botId: string, intent: string, channel?: string | false) => boolean {
      return (botId: string, intent: string, channel?: string | false) => {
        if (channel === false) {
          return false;
        } else if (
          !this.updates[botId] ||
          !this.updates[botId][intent] ||
          !this.updates[botId][intent]['' + channel]
        ) {
          return useGlimpseContentStore().isContentChanged(botId, intent, channel);
        } else {
          return !!this.updates[botId][intent]['' + channel].changed;
        }
      };
    },
    hasContent(): (botId: string, intent: string, channel?: string | null) => boolean {
      return (botId: string, intent: string, channel?: string | null) => {
        if (!this.contentDrafts[botId] || !this.contentDrafts[botId][intent]) {
          return false;
        }

        if (!Object.keys(this.contentDrafts[botId][intent]).length) return false;
        // Did not want to restrict based on channel
        if (channel === undefined) return true;

        return Object
          .values(this.contentDrafts[botId][intent])
          .some((box: any) => {
            // Channel is `null` (first channel), and box has no channel (implicit first channel).
            // Same rule as resetDrafts, so the reset button only shows for drafts it can delete.
            if (!channel) return !box.channel;
            // If we have a channel defined in both, and they are the same
            return channel === box.channel;
          });
      };
    },
    getContent(): (botId: string, intent: string, box: string) => any {
      return (botId: string, intent: string, box: string) => {
        // get drafts
        if (!this.contentDrafts?.[botId]?.[intent]?.[box]) {
          return null;
        }
        return this.contentDrafts[botId][intent][box];
      };
    },
    /**
     * Renamed from legacy getter `isAssistant` — collides with the state prop of the same name
     * (identity passthrough). The state prop keeps the name `isAssistant`; use that directly
     * instead of this getter where possible.
     */
    isAssistantGetter(): boolean {
      return this.isAssistant;
    },
    isCurrentBotDeployable(): boolean {
      // `bots` hasn't been converted to Pinia yet — resolve through the legacy bridge registry.
      // TODO: Phase 2 replaces this with `useBotsStore()` once `src/stores/bots.ts` exists.
      const botsStore = resolveLegacyNamespace('bots');
      const currentBotId = botsStore?.currentBotId;
      return this.isDeployable(currentBotId);
    },
  },
  actions: {
    async getDeployment({ botId }: { botId: string }) {
      let result = await ProductService.getDeployment(botId);

      if (result.status === 'ok' && result.deployable) {
        if (this.deployment[botId]) {
          // wenn aktuelle Publishdate > lastChangeDate
          if (moment(this.deployment[botId].lastPublishDate as any).isAfter(result.deployable.lastChangeDate)) {
            result.deployable.lastPublishDate = this.deployment[botId].lastPublishDate;
            result.deployable.isDeployable = false;
          }
        }
        this.setDeployment({ deployment: result.deployable, botId });
      }
    },
    /**
     * @param botId
     * @returns {Promise<{status: string, success: boolean}>}
     */
    deploy({ botId }: { botId: string }) {
      return ProductService.deploy(botId);
    },
    async fakeDeploy({ botId, lastChangeDate }: { botId: string; lastChangeDate: any }) {
      // send to slack!
      await ProductService.fakeDeploy(botId, lastChangeDate);
      this.setDeployment({
        deployment: {
          isDeployable: false,
          lastPublishDate: new Date(),
          lastChangeDate: false,
        },
        botId,
      });
    },
    /**
     * Merged from legacy action `update` (which computed `changed` via
     * `glimpseContent/isContentChanged` when not otherwise known) + legacy mutation `update` (which
     * applied the change to state). Sibling actions in this module call this directly with an
     * explicit `changed`, replicating their legacy `commit('update', {..., changed})` calls, which
     * bypassed the action's computation.
     */
    update(payload: { botId: string; name: string; channel?: string | null | false; changed?: boolean | null }) {
      const { botId, name, channel } = payload;
      let changed: boolean | null = payload.changed ?? null;

      if (payload.changed === undefined && botId && name) {
        // wenn origin vom draft abweicht, content Change Flag true
        changed = useGlimpseContentStore().isContentChanged(botId, name, channel as any);
      }

      // update counter
      let versionNumber = new Date().getTime();

      if (botId && name) {
        if (!this.updates[botId]) {
          this.updates = { ...this.updates, [botId]: {} };
        }

        if (!this.updates[botId][name]) {
          this.updates[botId] = { ...this.updates[botId], [name]: {} };
        }

        this.updates[botId][name] = {
          ...this.updates[botId][name],
          ['' + channel]: {
            changed,
          },
        };
      }

      this.updated = versionNumber;
    },
    async save({ botId, name, channel }: { botId: string; name: string; channel?: string | null | false }) {
      // only save if something available to save :)
      // `channel` may be `null` here (per this action's legacy payload type), but the
      // callees only ever check it for falsiness, so `null` and `false` behave identically.
      let draftBoxes = this.getContentBoxes(botId, name, channel as string | false | undefined);

      for (const box of draftBoxes) {
        // clone box json
        const body = JSON.parse(JSON.stringify(box));
        body.intent = name;

        await ContentService.saveContent(botId, box, body);
      }

      useGlimpseContentStore().resetOrigins({ botId, name, channel: channel as string | false | undefined });
      this.resetDrafts({ botId, name, channel: channel as string | false | undefined });
      this.update({ botId, name, channel, changed: false });
      this.getDeployment({ botId });
    },
    async createBox({ step, botId, intent, template, channel }: any) {
      let originContent = await ContentService.getContent(botId, template, intent, undefined, channel);
      if (
        originContent.result &&
        originContent.result.template &&
        originContent.result.box !== false &&
        originContent.result.onlyDraft
      ) {
        if (step !== undefined && step !== null) {
          originContent.result.step = step;
        }

        // TODO remove not reachable boxes

        originContent.result.onlyDraft = false;
        this.setContentDraft({
          content: originContent.result,
          botId,
          intent,
          box: originContent.result.box,
        });

        return originContent.result.box;
      }

      return false;
    },
    async updateBox({ step, botId, intent, template, channel, box }: any) {
      let originContent = await ContentService.getContent(botId, template, intent, box, channel);
      if (
        originContent.result &&
        originContent.result.template &&
        originContent.result.box !== false &&
        originContent.result.onlyDraft
      ) {
        if (step !== undefined && step !== null) {
          originContent.result.step = step;
        }

        originContent.result.onlyDraft = false;
        this.setContentDraft({
          content: originContent.result,
          botId,
          intent,
          box: originContent.result.box,
        });

        return originContent.result.box;
      }

      return false;
    },
    async copyOrigin({ botId, name, channel }: { botId: string; name: string; channel?: string | false }) {
      /**
       * it is called when switching to the edit mode.
       * it handles the origin -> draft copy, if no draft available
       *
       * TODO draft outdated feature
       */

      let draftBoxes = this.getContentBoxes(botId, name, channel);
      if (draftBoxes.length > 0) {
        return;
      }

      let allBoxes = useGlimpseContentStore().getContentBoxes(botId, name, channel);
      this.setContentDrafts({ drafts: allBoxes, botId });
    },
    async resetDraft({ botId, name, channel }: { botId: string; name: string; channel?: string | false }) {
      this.resetDrafts({ botId, name, channel });
      this.copyOrigin({ botId, name, channel });
      this.update({ botId, name, channel, changed: false });
    },
    async resetAll({ botId, name }: { botId: string; name: string }) {
      this.resetDrafts({ botId, name });
      this.resetUpdate({ botId, name });
      useGlimpseContentStore().resetOrigins({ botId, name });
      /*  this.copyOrigin({botId, name, channel});    */
      //this.update({botId, name, channel, changed: false});
    },
    setContentDraftIntent({
      botId,
      intent,
      boxes,
      channel,
    }: {
      botId: string;
      intent: string;
      boxes: any;
      channel?: string | null;
    }) {
      let keys = Object.keys(boxes);
      let drafts = keys.map((key) => {
        return boxes[key];
      });

      this.setContentDrafts({ botId, drafts });
      channel = channel || null;
      this.update({ botId, name: intent, channel, changed: true });
      return true;
    },
    deleteContentDrafts({
      botId,
      intentId,
      boxIds,
    }: {
      botId: string;
      intentId: string;
      boxIds: string[];
    }) {
      for (let boxId of boxIds) {
        if (boxId === intentId) {
          // Set initial box to empty object to avoid side effects
          this.contentDrafts[botId][intentId][boxId] = {};
          continue;
        }
        delete this.contentDrafts[botId][intentId][boxId];
      }
      return true;
    },
    /**
     * Converted from legacy mutation `setValue` (no matching legacy action existed).
     */
    setValue({ key, value }: { key: string; value: any }) {
      (this as any)[key] = value;
    },
    /**
     * Converted from legacy mutation `resetUpdate` (no matching legacy action existed).
     */
    resetUpdate({ botId, name }: { botId: string; name: string }) {
      if (botId && name) {
        if (this.updates[botId] && this.updates[botId][name]) {
          this.updates[botId] = { ...this.updates[botId], [name]: {} };
        }
      }
    },
    /**
     * Converted from legacy mutation `resetDrafts` (no matching legacy action existed).
     */
    resetDrafts({ botId, name, channel }: { botId: string; name: string; channel?: string | false }) {
      if (!this.contentDrafts?.[botId]?.[name]) return;
      if (channel === undefined) channel = false;

      // all draft boxes from channel
      let allBoxes = Object.values(this.contentDrafts[botId][name]).filter((box: any) => {
        if (channel === false) {
          return true;
        }
        if (!channel && box.channel) {
          return false;
        }
        if (!channel && !box.channel) {
          return true;
        }
        if (channel && box.channel && channel === box.channel) {
          return true;
        }

        return false;
      });

      if (allBoxes.length > 0) {
        // delete all drafts
        console.log('DELETE BOXES');
        for (let box of allBoxes as any[]) {
          delete this.contentDrafts[botId][name][box.box];
          this.contentDrafts[botId][name] = { ...this.contentDrafts[botId][name] };
        }
      }
    },
    /**
     * Converted from legacy mutation `setContentDraft` (no matching legacy action existed).
     */
    setContentDraft({
      content,
      botId,
      intent,
      box,
    }: {
      content: any;
      botId: string;
      intent: string;
      box: string;
    }) {
      const clone = JSON.parse(JSON.stringify(content));
      if (!this.contentDrafts[botId]) {
        this.contentDrafts = { ...this.contentDrafts, [botId]: {} };
      }

      if (!this.contentDrafts[botId][intent]) {
        this.contentDrafts[botId] = { ...this.contentDrafts[botId], [intent]: {} };
      }

      this.contentDrafts[botId][intent] = { ...this.contentDrafts[botId][intent], [box]: clone };
    },
    /**
     * Converted from legacy mutation `setContentDrafts` (no matching legacy action existed).
     */
    setContentDrafts({ botId, drafts }: { botId: string; drafts: any[] }) {
      for (let l of drafts) {
        const clone = JSON.parse(JSON.stringify(l));

        if (!this.contentDrafts[botId]) {
          this.contentDrafts = { ...this.contentDrafts, [botId]: {} };
        }

        if (!this.contentDrafts[botId][l.intent]) {
          this.contentDrafts[botId] = { ...this.contentDrafts[botId], [l.intent]: {} };
        }

        this.contentDrafts[botId][l.intent] = { ...this.contentDrafts[botId][l.intent], [l.box]: clone };
      }
    },
    /**
     * Converted from legacy mutation `setDeployment` (no matching legacy action existed).
     */
    setDeployment({ botId, deployment }: { botId: string; deployment: DeploymentInfo }) {
      if (!this.deployment[botId]) {
        this.deployment = { ...this.deployment, [botId]: deployment };
      } else {
        Object.keys(deployment).forEach((key) => {
          (this.deployment[botId] as any)[key] = (deployment as any)[key];
        });
      }
    },
  },
  vuexPersist: {
    local: true,
  },
});
